import React, { useCallback, useEffect, useState } from 'react'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../helpers/apiRequester'
import styles from './styles.module.scss'
import { BASE_URL, formatDateTime } from './shared'

interface ApiKeyRow {
    id: number
    name: string
    api_key: string
    status: 'active' | 'revoked'
    last_used_at: string | null
    created_at: string
    delivery_count: number
    rejection_count: number
}

const maskKey = (key: string): string =>
    key.length <= 8 ? '••••••••' : `${key.slice(0, 4)}…${'•'.repeat(8)}…${key.slice(-4)}`

export const ApiKeysTab = () => {
    const [keys, setKeys] = useState<ApiKeyRow[]>([])
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState<string | null>(null)
    const [revealed, setRevealed] = useState<Set<number>>(new Set())
    const [copiedId, setCopiedId] = useState<number | null>(null)

    // Create form
    const [newName, setNewName] = useState('')
    const [existingValue, setExistingValue] = useState('')
    const [creating, setCreating] = useState(false)
    const [createdKey, setCreatedKey] = useState<{ name: string; api_key: string } | null>(null)

    // Inline rename
    const [renamingId, setRenamingId] = useState<number | null>(null)
    const [renameValue, setRenameValue] = useState('')

    const fetchKeys = useCallback(async () => {
        setLoadError(null)
        try {
            const response = await api.post(`${BASE_URL}/api/fairclaim-api-keys`, {})
            if (!response.data?.success) throw new Error(response.data?.message || 'List request failed')
            setKeys(response.data.keys ?? [])
        } catch (error: any) {
            setLoadError(error?.response?.data?.message || error?.message || 'Could not load the API keys')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchKeys()
    }, [fetchKeys])

    const toggleReveal = (id: number) => {
        setRevealed((current) => {
            const next = new Set(current)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    const copyKey = async (id: number, value: string) => {
        try {
            await navigator.clipboard.writeText(value)
            setCopiedId(id)
            setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 2000)
        } catch {
            Report.failure('Copy failed', 'Could not write to the clipboard — reveal the key and copy it manually.', 'Okay')
        }
    }

    const createKey = async () => {
        const name = newName.trim()
        if (!name) {
            Report.failure('Name required', 'Give the key a sender name so deliveries can be attributed.', 'Okay')
            return
        }
        setCreating(true)
        try {
            const body: any = { name }
            if (existingValue.trim()) body.apiKey = existingValue.trim()
            const response = await api.post(`${BASE_URL}/api/fairclaim-api-key-create`, body)
            if (!response.data?.success) throw new Error(response.data?.message || 'Create request failed')
            setCreatedKey({ name: response.data.key.name, api_key: response.data.key.api_key })
            setNewName('')
            setExistingValue('')
            await fetchKeys()
        } catch (error: any) {
            Report.failure(
                'Could not create the key',
                error?.response?.data?.message || error?.message || 'Create request failed',
                'Okay'
            )
        } finally {
            setCreating(false)
        }
    }

    const updateKey = async (id: number, patch: { name?: string; status?: 'active' | 'revoked' }) => {
        const response = await api.post(`${BASE_URL}/api/fairclaim-api-key-update`, { id, ...patch })
        if (!response.data?.success) throw new Error(response.data?.message || 'Update request failed')
        await fetchKeys()
    }

    const confirmStatusChange = (key: ApiKeyRow) => {
        const revoking = key.status === 'active'
        Confirm.show(
            revoking ? `Revoke "${key.name}"?` : `Reactivate "${key.name}"?`,
            revoking
                ? 'Requests using this key will get 401 Unauthorized immediately (within a minute on other instances). ' +
                  'The key and its delivery history are kept and it can be reactivated later.'
                : 'Requests using this key will be accepted again.',
            revoking ? 'Revoke key' : 'Reactivate',
            'Cancel',
            async () => {
                try {
                    await updateKey(key.id, { status: revoking ? 'revoked' : 'active' })
                } catch (error: any) {
                    Report.failure(
                        'Update failed',
                        error?.response?.data?.message || error?.message || 'Update request failed',
                        'Okay'
                    )
                }
            },
            () => undefined,
            revoking ? { titleColor: '#c62828', okButtonBackground: '#c62828' } : undefined
        )
    }

    const startRename = (key: ApiKeyRow) => {
        setRenamingId(key.id)
        setRenameValue(key.name)
    }

    const submitRename = async (id: number) => {
        const name = renameValue.trim()
        setRenamingId(null)
        if (!name || name === keys.find((k) => k.id === id)?.name) return
        try {
            await updateKey(id, { name })
        } catch (error: any) {
            Report.failure(
                'Rename failed',
                error?.response?.data?.message || error?.message || 'Update request failed',
                'Okay'
            )
        }
    }

    return (
        <div>
            <p className={styles.footnote}>
                Every sender posting to <code>POST /fairclaim/webhook</code> must authenticate with one of these
                keys (<code>Authorization: Bearer &lt;key&gt;</code>). Each key carries a sender name, and every
                accepted or rejected delivery is recorded against it — so you always know who sent what. Unknown
                or revoked keys get <strong>401 Unauthorized</strong>. Keys are never deleted, only revoked, so
                the sending history stays attributable.
            </p>

            <div className={styles.keysCreateCard}>
                <h4>Issue a new key</h4>
                <div className={styles.keysCreateForm}>
                    <label>
                        <span>Sender name</span>
                        <input
                            placeholder="e.g. production"
                            value={newName}
                            maxLength={100}
                            onChange={(e) => setNewName(e.target.value)}
                        />
                    </label>
                    <label>
                        <span>Existing key value (optional)</span>
                        <input
                            placeholder="Leave empty to generate a fresh key"
                            value={existingValue}
                            onChange={(e) => setExistingValue(e.target.value)}
                        />
                    </label>
                    <button className={styles.exportButton} onClick={createKey} disabled={creating}>
                        {creating ? 'Creating…' : existingValue.trim() ? 'Register existing key' : 'Generate key'}
                    </button>
                </div>
                <span className={styles.footnote}>
                    Paste an existing value to register a key that is already out with a sender — it becomes a
                    named, attributable key without the sender changing anything.
                </span>
                {createdKey && (
                    <div className={styles.newKeyPanel}>
                        <strong>Key for “{createdKey.name}” ready — send it to the client:</strong>
                        <div className={styles.newKeyRow}>
                            <code>{createdKey.api_key}</code>
                            <button
                                className={styles.linkButton}
                                onClick={() => copyKey(-1, createdKey.api_key)}
                            >
                                {copiedId === -1 ? 'Copied!' : 'Copy'}
                            </button>
                            <button className={styles.linkButton} onClick={() => setCreatedKey(null)}>
                                Dismiss
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {loadError ? (
                <p className={styles.errorText}>{loadError}</p>
            ) : (
                <table className={styles.dataTable}>
                    <thead>
                        <tr>
                            <th>Sender</th>
                            <th>Key</th>
                            <th>Status</th>
                            <th>Deliveries</th>
                            <th>Rejections</th>
                            <th>Last used</th>
                            <th>Created</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {keys.map((key) => (
                            <tr key={key.id}>
                                <td>
                                    {renamingId === key.id ? (
                                        <input
                                            className={styles.renameInput}
                                            value={renameValue}
                                            maxLength={100}
                                            autoFocus
                                            onChange={(e) => setRenameValue(e.target.value)}
                                            onBlur={() => submitRename(key.id)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') submitRename(key.id)
                                                if (e.key === 'Escape') setRenamingId(null)
                                            }}
                                        />
                                    ) : (
                                        <strong>{key.name}</strong>
                                    )}
                                </td>
                                <td>
                                    <code className={styles.keyCode}>
                                        {revealed.has(key.id) ? key.api_key : maskKey(key.api_key)}
                                    </code>
                                </td>
                                <td>
                                    <span className={key.status === 'active' ? styles.chipAccepted : styles.chipRejected}>
                                        {key.status}
                                    </span>
                                </td>
                                <td>{key.delivery_count}</td>
                                <td>{key.rejection_count}</td>
                                <td>{formatDateTime(key.last_used_at)}</td>
                                <td>{formatDateTime(key.created_at)}</td>
                                <td className={styles.keyActions}>
                                    <button className={styles.linkButton} onClick={() => toggleReveal(key.id)}>
                                        {revealed.has(key.id) ? 'Hide' : 'Reveal'}
                                    </button>
                                    <button className={styles.linkButton} onClick={() => copyKey(key.id, key.api_key)}>
                                        {copiedId === key.id ? 'Copied!' : 'Copy'}
                                    </button>
                                    <button className={styles.linkButton} onClick={() => startRename(key)}>
                                        Rename
                                    </button>
                                    <button
                                        className={key.status === 'active' ? styles.revokeButton : styles.linkButton}
                                        onClick={() => confirmStatusChange(key)}
                                    >
                                        {key.status === 'active' ? 'Revoke' : 'Reactivate'}
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {!loading && keys.length === 0 && (
                            <tr>
                                <td colSpan={8} className={styles.emptyCell}>
                                    No API keys yet — the registry seeds itself with the original shared token on
                                    the first webhook call, or issue a key above.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            )}
        </div>
    )
}
